import type { ReactElement } from 'react';
import type { ILocaleConfig } from '@/types/localeTypes';
import { useTranslations } from '@/hooks/useTranslations';
import styles from './LocaleOption.module.scss';

interface ILocaleOptionProps {
  locale: ILocaleConfig;
  onSelect: (localeCode: string) => void;
}

/**
 * Individual locale option component
 * Renders as an accessible link with ARIA labels
 */
export function LocaleOption({ locale, onSelect }: ILocaleOptionProps): ReactElement {
  const { t } = useTranslations();
  
  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>): void => {
    event.preventDefault();
    onSelect(locale.code);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLAnchorElement>): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelect(locale.code);
    }
  };

  return (
    <a
      href={`/${locale.code}`}
      className={styles.localeOption}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-label={t("localePicker.selectLocale", { localeName: locale.name })}
      role="button"
      tabIndex={0}
    >
      {locale.flagIcon && <span className={styles.flag} aria-hidden="true">{locale.flagIcon}</span>}
      <span className={styles.name}>{locale.name}</span>
    </a>
  );
}
